import { makeAutoObservable } from 'mobx'
import { i18n } from '@/i18n'
import { readJSON, writeJSON } from '@/lib/storage'
import type { ContentLocale } from '@/types/api'

export type ThemePreference = 'system' | 'light' | 'dark'
export type Density = 'compact' | 'comfortable'

// Stored as a raw string because index.html reads it before the bundle loads.
function readTheme(): ThemePreference {
  try {
    const value = localStorage.getItem('mf.ui.theme')
    return value === 'light' || value === 'dark' || value === 'system' ? value : 'light'
  } catch {
    return 'light'
  }
}

const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null

export class UiStore {
  theme: ThemePreference = readTheme()
  systemDark = media?.matches ?? false
  sidebarCollapsed = readJSON<boolean>('mf.ui.sidebar', false)
  mobileNavOpen = false
  density: Density = readJSON<Density>('mf.ui.density', 'compact')
  commandOpen = false

  constructor() {
    makeAutoObservable(this)
    media?.addEventListener('change', (event) => this.setSystemDark(event.matches))
    this.applyTheme()
  }

  get resolvedTheme(): 'light' | 'dark' {
    if (this.theme === 'system') return this.systemDark ? 'dark' : 'light'
    return this.theme
  }

  setTheme(theme: ThemePreference, animate = true) {
    this.theme = theme
    try {
      localStorage.setItem('mf.ui.theme', theme)
    } catch {
      /* storage unavailable */
    }
    this.applyTheme(animate)
  }

  cycleTheme() {
    this.setTheme(this.resolvedTheme === 'dark' ? 'light' : 'dark')
  }

  setSidebarCollapsed(collapsed: boolean) {
    this.sidebarCollapsed = collapsed
    writeJSON('mf.ui.sidebar', collapsed)
  }

  toggleSidebar() {
    this.setSidebarCollapsed(!this.sidebarCollapsed)
  }

  setMobileNav(open: boolean) {
    this.mobileNavOpen = open
  }

  /** One language drives both the interface and record names. */
  get contentLocale(): ContentLocale {
    return i18n.locale
  }

  setContentLocale(locale: ContentLocale) {
    void i18n.setLocale(locale)
  }

  setDensity(density: Density) {
    this.density = density
    writeJSON('mf.ui.density', density)
  }

  setCommandOpen(open: boolean) {
    this.commandOpen = open
  }

  private setSystemDark(dark: boolean) {
    this.systemDark = dark
    this.applyTheme()
  }

  /** A user-initiated switch cross-fades the whole UI via the View Transitions API. */
  private applyTheme(animate = false) {
    const theme = this.resolvedTheme
    const apply = () => {
      document.documentElement.dataset.theme = theme
    }
    const reduced = document.documentElement.dataset.motion === 'reduced'
    // Cross-fade only when the look really changes (light → system on a light OS is a no-op).
    const changes = document.documentElement.dataset.theme !== theme
    if (animate && changes && !reduced && typeof document.startViewTransition === 'function') document.startViewTransition(apply)
    else apply()
  }
}
